/**
 * THE game-rating engine — the one place that turns what we already measure
 * about a game into a single comparable number.
 *
 * WHY THIS EXISTS (2026-08-23): the platform had two quality signals and neither
 * could rank the catalogue on its own.
 *
 *  - The Editors' Pick reviewer scores an ABSOLUTE 0-100 from a text DOSSIER
 *    (title, description, design doc, engagement counters). It reads the pitch,
 *    never the running game — so a creation whose controls are dead at the
 *    source scored 70 and wore a laurel, while the hand-written natives, which
 *    have no design doc to show, scored 22-42. It measures how good the idea
 *    reads and how much paperwork exists, which is APPEAL, not quality.
 *
 *  - The rendered playtest audit drives the real build in a browser and reports
 *    per-criterion pass/fail with evidence. It knows whether pressing a key does
 *    anything. It says nothing about whether the game is worth playing.
 *
 * So: APPEAL ranks the good ones against each other, PLAYABILITY floors the
 * broken ones. Multiplying is deliberate rather than averaging — a game you
 * cannot play is not "a bit worse than average", and an average of 70 and 0 is
 * 35, which would still outrank a modest game that actually works.
 *
 * THE RULE THAT MATTERS MOST: a measurement that did not happen is never
 * evidence of failure. `VersionGates.checks` already argues this for gates
 * ("averaging those in as passes is how a gate comes to look healthier than it
 * is"); the inverse trap is worse here, because scoring an unmeasured game 0
 * would bury a creator's work for the platform's own omission. Unknown
 * playability leaves the appeal score alone and says so in `basis`.
 */
import type { PlaytestAuditReport, PlaytestTierReport } from '../types.js';

/**
 * Criteria whose failure means the game is not playable at all. These are not
 * severe penalties — they are a floor, because no amount of polish elsewhere
 * compensates. Each is quoted with the evidence string the audit emits, so the
 * reason a game floors is auditable from this file alone:
 *
 *  - `controls-effect` "no held key or pointer tap produced a single command —
 *    the adapter never mapped input to a command, so the controls are dead at
 *    the source"
 *  - `boot` the game never reached playable state.
 *  - `content-moderation` the rendered game showed something that must not be
 *    on a public shelf. Not a playability problem — a publishing one — but it
 *    floors for the same reason: nothing else about the game can outweigh it.
 */
const LEGACY_RATING_BLOCKERS: readonly string[] = [
  'boot',
  'controls-effect',
  'content-moderation',
];

/** Version 10 distinguishes command delivery from consequential player effects. */
const PLAYER_PROOF_BLOCKERS: readonly string[] = [
  ...LEGACY_RATING_BLOCKERS,
  'controls-delivery',
  'interaction-effect',
  'interaction-legibility',
  'interaction-coverage',
];

export const RATING_BLOCKERS: readonly string[] = [
  ...PLAYER_PROOF_BLOCKERS,
  'object-visibility',
  'terrain-scenery-separation',
];

/** Historical visual findings retain the meaning of their original instrument. */
export function ratingBlockersForAudit(auditVersion?: number): readonly string[] {
  if ((auditVersion ?? 0) >= 11) return RATING_BLOCKERS;
  return (auditVersion ?? 0) >= 10 ? PLAYER_PROOF_BLOCKERS : LEGACY_RATING_BLOCKERS;
}

/**
 * Penalty per failed non-blocker criterion. Weighted by how much the failure
 * costs a first-time player, not by how hard it is to fix: an unreachable end
 * screen ruins the session's payoff, a slightly illegible mobile HUD annoys.
 * A criterion absent from this table costs {@link DEFAULT_PENALTY} — an
 * unrecognized name is a real finding we have not tuned, not a free pass.
 */
export const RATING_PENALTIES: Readonly<Record<string, number>> = {
  /**
   * `movement` "2s of bot-driven play changed nothing on screen". Heavily
   * penalized but deliberately NOT a blocker, because it provably
   * false-positives: it compares net on-screen displacement across a 2-second
   * window, so a fast game that RESETS entities to fixed spawn points can look
   * motionless while playing perfectly.
   *
   * Paid for on `atari-combat-tanks` v14 (2026-08-23). Its audit failed
   * `movement`, so an earlier draft of this table floored it to 0 and dropped it
   * from the gallery. Driving the published sim headlessly showed the opposite:
   * holding drive moved the tank 2.20 → 6.73 world units in 1.3s and then
   * stopped correctly against an obstacle, its own bot scored 11-3 in 30s, and
   * two builder gates on the SAME build (`winnability`, `inertResolve`) both
   * passed. What the probe actually caught was the bot landing a hit inside the
   * sample window, after which both tanks respawn at their spawn points — so
   * start-vs-end displacement is ~0.1 units.
   *
   * `controls-effect` stays a blocker because it measures the INPUT PATH
   * directly ("the adapter never mapped input to a command") and has no such
   * failure mode. A `movement` failure means "look at this", not "it is dead".
   */
  movement: 30,
  // Nothing renders / renders wrong: the game looks broken even if it runs.
  'entities-rendered': 20,
  'art-bound': 15,
  'textures-resolved': 15,
  'object-visibility': 12,
  // The session cannot resolve, or it throws while you play.
  'no-page-errors': 18,
  'end-screen': 12,
  'end-screen-complete': 10,
  // Presentation: noticeable, not fatal.
  'tick-rate': 8,
  'color-contrast': 6,
  'art-alignment': 6,
  'menu-readable': 6,
  'hud-presence': 5,
  'size-sanity': 5,
  'text-overlap': 5,
  'mobile-hud-legible': 5,
  'hud-mobile-legible': 4,
  'mobile-layout': 4,
};

/** Cost of a failed criterion this table has not been tuned for. */
export const DEFAULT_PENALTY = 5;

/**
 * The score an unreviewed game is ranked at — the middle of the reviewer's own
 * "solid but unremarkable" band (50-69). A game nobody has reviewed yet is
 * average by assumption, never bad: not being reviewed is the platform's
 * backlog, not the creator's failing.
 */
export const NEUTRAL_APPEAL = 55;

/** Score at or above which a game is worth showcasing (the card's quality mark
 *  and the floor for a curated shelf). Set against the reviewer's own anchors:
 *  70+ is "strong, clearly above average". */
export const SHOWCASE_SCORE = 70;

/** How much of the rating we were actually able to measure. */
export type RatingBasis =
  /** Both halves: a usable audit and a review of this version. */
  | 'full'
  /** Audit only — never reviewed, so appeal is the neutral assumption. */
  | 'unreviewed'
  /** Review only — no usable audit, so playability did not move the score. */
  | 'unaudited'
  /** Neither. The score is the neutral assumption and means very little. */
  | 'unmeasured';

/** One game's rating, as stamped on the creation doc. */
export interface CreationRating {
  /** 0-100, the sort key. 0 means BLOCKED, not "scored badly". */
  score: number;
  /** 0-100 from the audit; null when nothing usable was measured. */
  playability: number | null;
  /** The reviewer's absolute score; null when this version was never reviewed. */
  appeal: number | null;
  /** Failed blocker criteria — non-empty means the game is unplayable. */
  blockers: string[];
  /** Other failed criteria, worst-penalty first. */
  findings: string[];
  /** How much of this was actually measured — never infer it from the numbers. */
  basis: RatingBasis;
  /** The creation's activeVersion when this was computed. */
  ratedVersion: number;
  ratedAt: number;
  /** Audit instrument version (`AUDIT_SCHEMA_VERSION`) behind `playability`.
   *  Two ratings from different instruments are two different readings. */
  auditVersion?: number;
}

export interface EvaluateRatingInput {
  /** The audit stamped on the ACTIVE version's gates; null/undefined = none. */
  audit?: PlaytestAuditReport | null;
  /** The reviewer's score for this creation, or null if never reviewed. */
  appeal?: number | null;
  /** The creation's activeVersion (stamped for staleness checks). */
  version: number;
  now: number;
}

/** Tiers that actually measured something. `infra-open` means the ENVIRONMENT
 *  failed (browser launch, VLM transport) — the game was never judged, so the
 *  tier is dropped rather than read as a bad result. A green tier may contain
 *  only unjudged coverage; measurement requires an actual boolean criterion. */
function usableTiers(audit: PlaytestAuditReport | null | undefined): PlaytestTierReport[] {
  return [audit?.tier1, audit?.tier2].filter(
    (t): t is PlaytestTierReport => !!t && t.verdict !== 'infra-open'
      && Object.values(t.criteria ?? {}).some((criterion) => typeof criterion?.pass === 'boolean'),
  );
}

/**
 * Compute a game's rating. Pure: the same inputs always give the same rating,
 * so the ops backfill, the scheduled re-rate and any test agree by construction.
 */
export function evaluateRating(input: EvaluateRatingInput): CreationRating {
  const tiers = usableTiers(input.audit);
  const appeal = typeof input.appeal === 'number' ? input.appeal : null;

  const blockers: string[] = [];
  const scored: Array<{ name: string; penalty: number }> = [];
  for (const tier of tiers) {
    for (const [name, criterion] of Object.entries(tier.criteria ?? {})) {
      if (criterion?.pass !== false) continue;
      if (ratingBlockersForAudit(tier.auditVersion).includes(name)) {
        if (!blockers.includes(name)) blockers.push(name);
        continue;
      }
      if (scored.some((s) => s.name === name)) continue; // the same criterion in both tiers is one finding
      scored.push({ name, penalty: RATING_PENALTIES[name] ?? DEFAULT_PENALTY });
    }
  }
  scored.sort((a, b) => b.penalty - a.penalty || a.name.localeCompare(b.name));
  const findings = scored.map((s) => s.name);

  const measured = tiers.length > 0;
  const playability = measured
    ? blockers.length > 0
      ? 0
      : Math.max(0, 100 - scored.reduce((sum, s) => sum + s.penalty, 0))
    : null;

  const basis: RatingBasis = measured
    ? appeal === null
      ? 'unreviewed'
      : 'full'
    : appeal === null
      ? 'unmeasured'
      : 'unaudited';

  // Unknown playability must not multiply the score down — see the header.
  const score =
    playability === null
      ? Math.round(appeal ?? NEUTRAL_APPEAL)
      : Math.round((appeal ?? NEUTRAL_APPEAL) * (playability / 100));

  return {
    score,
    playability,
    appeal,
    blockers,
    findings,
    basis,
    ratedVersion: input.version,
    ratedAt: input.now,
    ...(tiers[0]?.auditVersion !== undefined ? { auditVersion: tiers[0].auditVersion } : {}),
  };
}

/** True when a game is unplayable — the demote-to-bottom predicate. Reads the
 *  blocker list rather than `score === 0` so a game that merely scored terribly
 *  is never mistaken for a broken one. */
export function isBlocked(rating: CreationRating | undefined): boolean {
  return (rating?.blockers.length ?? 0) > 0;
}

/** True when a game is good enough to showcase (card mark, curated shelves).
 *  Requires a real measurement: a game we never audited is not promoted on the
 *  strength of an assumption. */
export function isShowcase(rating: CreationRating | undefined): boolean {
  if (!rating || isBlocked(rating)) return false;
  return rating.score >= SHOWCASE_SCORE && (rating.basis === 'full' || rating.basis === 'unreviewed');
}
